import { useEffect, useState } from "react";
import {
  fetchFreeDemoRequests, cancelFreeDemoRequest,
  fetchFreeDemoSessions, scheduleFreeDemoSession,
  rescheduleFreeDemoSession, updateFreeDemoSessionStatus,
} from "./adminFreeDemoService";
import "../courses/adminCourses.css";

const STATUS_BADGE = {
  PENDING:   "ac-badge-yellow",
  SCHEDULED: "ac-badge-blue",
  COMPLETED: "ac-badge-green",
  CANCELLED: "ac-badge-red",
};

const SLOT_LABEL = { MORNING: "🌅 Morning", AFTERNOON: "☀️ Afternoon", EVENING: "🌙 Evening" };

const fmtDate = (iso) => iso
  ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true })
  : "—";

/* ── Schedule / Reschedule Modal ── */
const ScheduleModal = ({ session, onClose, onDone }) => {
  const isReschedule = Boolean(session?.id);
  const [scheduledAt, setScheduledAt] = useState(
    session?.scheduledAt ? new Date(session.scheduledAt).toISOString().slice(0, 16) : ""
  );
  const [durationMins, setDurationMins] = useState(session?.durationMins ?? 30);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!scheduledAt) { setError("Please select a date and time."); return; }
    setError(""); setSaving(true);
    try {
      const iso = new Date(scheduledAt).toISOString();
      if (isReschedule) {
        await rescheduleFreeDemoSession(session.id, { scheduledAt: iso, durationMins: Number(durationMins) });
      } else {
        await scheduleFreeDemoSession({ requestId: session.requestId, scheduledAt: iso, durationMins: Number(durationMins) });
      }
      onDone();
    } catch (err) {
      setError(err.response?.data?.message || "Failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: "2rem", width: "100%", maxWidth: 420, boxShadow: "0 10px 40px rgba(0,0,0,0.2)" }}>
        <h3 style={{ margin: "0 0 1.25rem", fontSize: "1.1rem" }}>
          {isReschedule ? "Reschedule Session" : "Schedule Demo Session"}
        </h3>
        {error && <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", color: "#dc2626", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: "0.85rem" }}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="ac-form-group">
            <label>Date &amp; Time</label>
            <input className="ac-input" type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} required />
          </div>
          <div className="ac-form-group" style={{ marginTop: 12 }}>
            <label>Duration (minutes)</label>
            <input className="ac-input" type="number" min="15" max="120" value={durationMins} onChange={(e) => setDurationMins(e.target.value)} style={{ maxWidth: 120 }} />
          </div>
          <div className="ac-form-actions">
            <button type="submit" className="ac-btn ac-btn-primary" disabled={saving}>
              {saving ? "Saving…" : isReschedule ? "Reschedule" : "Schedule"}
            </button>
            <button type="button" className="ac-btn ac-btn-ghost" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── Requests Tab ── */
const RequestsTab = ({ onSchedule }) => {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter]     = useState("");
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");

  const load = () => {
    setLoading(true);
    fetchFreeDemoRequests(filter || undefined)
      .then((res) => setRequests(res.data || []))
      .catch(() => setError("Failed to load requests."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const handleCancel = async (id) => {
    if (!window.confirm("Cancel this demo request? A cancellation email will be sent to the user.")) return;
    try {
      await cancelFreeDemoRequest(id);
      load();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to cancel.");
    }
  };

  return (
    <div>
      {/* Filter */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {["", "PENDING", "SCHEDULED", "COMPLETED", "CANCELLED"].map((s) => (
          <button key={s} className={`ac-btn ac-btn-sm ${filter === s ? "ac-btn-primary" : "ac-btn-ghost"}`} onClick={() => setFilter(s)}>
            {s || "All"}
          </button>
        ))}
      </div>

      {loading && <div className="ac-loading">Loading…</div>}
      {error   && <div style={{ color: "#dc2626", padding: "1rem" }}>{error}</div>}

      {!loading && requests.length === 0 && (
        <div className="ac-empty">No requests found.</div>
      )}

      {!loading && requests.length > 0 && (
        <div className="ac-table-wrap">
          <table className="ac-table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Slot</th>
                <th>Message</th>
                <th>Status</th>
                <th>Requested</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => (
                <tr key={r.id}>
                  <td>
                    <strong>{r.userName}</strong>
                    <div style={{ fontSize: "0.78rem", color: "#6b7280" }}>{r.userEmail}</div>
                    {r.userPhone && <div style={{ fontSize: "0.78rem", color: "#6b7280" }}>{r.userPhone}</div>}
                  </td>
                  <td>{r.preferredSlot ? SLOT_LABEL[r.preferredSlot] : <span style={{ color: "#9ca3af" }}>—</span>}</td>
                  <td style={{ maxWidth: 200, fontSize: "0.85rem", color: "#374151" }}>
                    {r.message || <span style={{ color: "#9ca3af" }}>—</span>}
                  </td>
                  <td><span className={`ac-badge ${STATUS_BADGE[r.status]}`}>{r.status}</span></td>
                  <td style={{ fontSize: "0.82rem" }}>{fmtDate(r.requestedAt)}</td>
                  <td>
                    <div className="ac-actions">
                      {r.status === "PENDING" && (
                        <>
                          <button className="ac-btn ac-btn-primary ac-btn-sm" onClick={() => onSchedule({ requestId: r.id, userName: r.userName })}>
                            Schedule
                          </button>
                          <button className="ac-btn ac-btn-danger ac-btn-sm" onClick={() => handleCancel(r.id)}>
                            Cancel
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ── Sessions Tab ── */
const SessionsTab = () => {
  const [sessions, setSessions] = useState([]);
  const [filter, setFilter]     = useState("");
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(null); // session object for reschedule

  const load = () => {
    setLoading(true);
    fetchFreeDemoSessions(filter || undefined)
      .then((res) => setSessions(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const handleStatus = async (sessionId, to) => {
    const label = to === "COMPLETED" ? "mark as completed" : "cancel";
    if (!window.confirm(`Are you sure you want to ${label} this session?`)) return;
    try {
      await updateFreeDemoSessionStatus(sessionId, to);
      load();
    } catch (err) {
      alert(err.response?.data?.message || "Failed.");
    }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {["", "SCHEDULED", "COMPLETED", "CANCELLED"].map((s) => (
          <button key={s} className={`ac-btn ac-btn-sm ${filter === s ? "ac-btn-primary" : "ac-btn-ghost"}`} onClick={() => setFilter(s)}>
            {s || "All"}
          </button>
        ))}
      </div>

      {loading && <div className="ac-loading">Loading…</div>}

      {!loading && sessions.length === 0 && (
        <div className="ac-empty">No sessions found.</div>
      )}

      {!loading && sessions.length > 0 && (
        <div className="ac-table-wrap">
          <table className="ac-table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Scheduled At</th>
                <th>Duration</th>
                <th>Zoom</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr key={s.id}>
                  <td>
                    <strong>{s.request?.userName}</strong>
                    <div style={{ fontSize: "0.78rem", color: "#6b7280" }}>{s.request?.userEmail}</div>
                  </td>
                  <td style={{ fontSize: "0.85rem" }}>{fmtDate(s.scheduledAt)}</td>
                  <td style={{ fontSize: "0.85rem" }}>{s.durationMins} min</td>
                  <td>
                    {s.zoomJoinUrl
                      ? <a href={s.zoomJoinUrl} target="_blank" rel="noreferrer" style={{ fontSize: "0.82rem", color: "#2563eb" }}>Join Link</a>
                      : <span style={{ color: "#9ca3af", fontSize: "0.82rem" }}>—</span>}
                    {s.zoomPassword && <div style={{ fontSize: "0.75rem", color: "#6b7280" }}>pwd: {s.zoomPassword}</div>}
                  </td>
                  <td><span className={`ac-badge ${STATUS_BADGE[s.status]}`}>{s.status}</span></td>
                  <td>
                    <div className="ac-actions">
                      {s.status === "SCHEDULED" && (
                        <>
                          <button className="ac-btn ac-btn-warning ac-btn-sm" onClick={() => setModal(s)}>Reschedule</button>
                          <button className="ac-btn ac-btn-success ac-btn-sm" onClick={() => handleStatus(s.id, "COMPLETED")}>Complete</button>
                          <button className="ac-btn ac-btn-danger ac-btn-sm" onClick={() => handleStatus(s.id, "CANCELLED")}>Cancel</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <ScheduleModal
          session={modal}
          onClose={() => setModal(null)}
          onDone={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
};

/* ── Main Page ── */
const AdminFreeDemoPage = () => {
  const [tab, setTab]       = useState("requests");
  const [modal, setModal]   = useState(null); // { requestId, userName }
  const [, setRefresh]      = useState(0);

  return (
    <div>
      <div className="ac-header">
        <h2>Free Demo Requests</h2>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: "2px solid #e5e7eb" }}>
        {[["requests", "📋 Requests"], ["sessions", "📅 Sessions"]].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            style={{ padding: "8px 20px", border: "none", background: "none", fontWeight: 700, fontSize: "0.9rem", cursor: "pointer", borderBottom: tab === key ? "2px solid #2563eb" : "2px solid transparent", color: tab === key ? "#2563eb" : "#6b7280", marginBottom: -2 }}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "requests" && (
        <RequestsTab onSchedule={(data) => setModal(data)} />
      )}
      {tab === "sessions" && <SessionsTab />}

      {modal && (
        <ScheduleModal
          session={modal}
          onClose={() => setModal(null)}
          onDone={() => { setModal(null); setRefresh((n) => n + 1); setTab("sessions"); }}
        />
      )}
    </div>
  );
};

export default AdminFreeDemoPage;
